import { CompassOutlined } from '@ant-design/icons';
import { useDraggableFloat } from 'common/components/PageTour/useDraggableFloat';
import { CSSProperties } from 'react';

const STORAGE_KEY = 'tf_guide_button_pos';
// A rough estimate of the rendered pill's footprint, used only to keep it
// clamped inside the viewport while dragging — not an exact box.
const SIZE = { width: 132, height: 34 };

type PropsType = {
    remaining: number;
    onClick: () => void;
};

// Same drag-anywhere + remembered-position + hover-vs-hold cursor behavior as
// the per-page tour button (common/components/PageTour/DraggableTourButton),
// kept as a labeled pill (smaller than the original) so "Take a Tour" stays
// readable rather than shrinking to an icon-only badge.
const DraggableGuideButton = ({ remaining, onClick }: PropsType) => {
    const { pos, cursor, handlers, wasDragged } = useDraggableFloat(STORAGE_KEY, SIZE, () => ({
        // Matches the old fixed spot (bottom-28 right-6 ≈ 112px / 24px) so a
        // user who never drags it sees no change.
        x: window.innerWidth - 24 - SIZE.width,
        y: window.innerHeight - 112 - SIZE.height,
    }));

    const style: CSSProperties = {
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        border: 'none',
        borderRadius: 999,
        background: '#0B172B',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor,
        zIndex: 1100,
        touchAction: 'none',
        userSelect: 'none',
    };

    return (
        <button
            type="button"
            title="Take a Tour"
            aria-label="Take a Tour"
            style={style}
            {...handlers}
            onClick={() => {
                if (!wasDragged()) onClick();
            }}
        >
            <CompassOutlined />
            Take a Tour
            {remaining > 0 && (
                <span
                    style={{
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.2)',
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: '16px',
                        padding: '0 6px',
                    }}
                >
                    {remaining}
                </span>
            )}
        </button>
    );
};

export default DraggableGuideButton;
